import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import angleUp from 'assets/angle-up.svg';
import selectedWorkspaceIcon from 'assets/workspace-check.svg';

import { ROUTES } from 'routes/route.constants';

import { getInitials } from 'utlis/globalFunctions';

import styles from './WorkspaceSwitcher.module.scss';

const WorkspaceSwitcher = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const { workspaces, selectedWorkspace, selectWorkspace, isLoadingWorkspaces } = useWorkspace();
  const authUser = useSelector(state => state.userStore);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  const toggleDropdown = () => setIsDropdownOpen(val => !val);

  const handleSelect = uuid => {
    selectWorkspace(uuid);
    setIsDropdownOpen(false);
    setActiveTooltip(null);
  };

  const handleNameMouseEnter = (e, name) => {
    const el = e.currentTarget;

    if (el.scrollWidth <= el.offsetWidth) {
      return;
    }

    const rect = el.getBoundingClientRect();

    setActiveTooltip({
      text: name,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleNameMouseLeave = () => setActiveTooltip(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <div ref={containerRef} className="mx-12 my-16" style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={isLoadingWorkspaces ? undefined : toggleDropdown}
        disabled={isLoadingWorkspaces}
        className={cn(
          'flex items-center gap-12 px-12 py-2 rounded-md w-full',
          'cursor-pointer border-none bg-transparent hover:bg-gray-50'
        )}
      >
        {isLoadingWorkspaces
          ? (
            <>
              <div className={styles['skeleton-icon']} />
              {!isCollapsed && <div className={styles['skeleton-text']} />}
            </>
          )
          : (
            <>
              {selectedWorkspace?.avatar_url
                ? (
                  <div className={cn(styles['workspace-icon'], styles['avatar'])}>
                    <img src={selectedWorkspace.avatar_url} alt="workspace" />
                  </div>
                )
                : (
                  <div className={styles['row-initials']}>
                    {getInitials(selectedWorkspace?.name)}
                  </div>
                )}

              {!isCollapsed && (
                <>
                  <span
                    className="text-sm font-medium text-gray-900 flex-1 text-left truncate min-w-0"
                    onMouseEnter={e => handleNameMouseEnter(e, selectedWorkspace?.name ?? '')}
                    onMouseLeave={handleNameMouseLeave}
                  >
                    {selectedWorkspace?.name ?? ''}
                  </span>
                  <div className={cn(styles.chevron, { [styles['chevron-open']]: isDropdownOpen })}>
                    <img src={angleUp} alt="" />
                  </div>
                </>
              )}
            </>
          )}
      </button>

      {activeTooltip && (
        <div
          className={styles['workspace-name-tooltip']}
          style={{
            position: 'fixed',
            left: activeTooltip.x,
            top: activeTooltip.y,
            transform: 'translateX(-50%) translateY(calc(-100% - 8px))',
            zIndex: 2000,
          }}
        >
          {activeTooltip.text}
        </div>
      )}

      {isDropdownOpen && (
        <div className={styles.dropdown}>
          <span className={styles['dropdown-heading']}>
            Member of
          </span>

          <div className={styles['workspace-list']}>
            {workspaces.map(workspace => {
              const isSelected = workspace.uuid === selectedWorkspace?.uuid;

              return (
                <button
                  key={workspace.uuid}
                  type="button"
                  className={cn(styles['workspace-row'], { [styles['workspace-row-selected']]: isSelected })}
                  onClick={() => handleSelect(workspace.uuid)}
                >
                  <div className={styles['row-left']}>
                    {workspace.avatar_url
                      ? (
                        <div className={cn(styles['row-workspace-icon'], styles['avatar'])}>
                          <img src={workspace.avatar_url} alt="" />
                        </div>
                      )
                      : (
                        <div className={styles['row-initials']}>
                          {getInitials(workspace.name)}
                        </div>
                      )}
                    <span
                      className={styles['row-name']}
                      onMouseEnter={e => handleNameMouseEnter(e, workspace.name)}
                      onMouseLeave={handleNameMouseLeave}
                    >
                      {workspace.name}
                    </span>
                  </div>

                  <div className={styles.checkmark}>
                    {isSelected && (
                      <img src={selectedWorkspaceIcon} alt="selected" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {authUser?.is_internal_user && (
            <>
              <div className={styles['dropdown-divider']} />
              <button
                type="button"
                className={styles['create-workspace-btn']}
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate(ROUTES.TEAM_CREATE_WORKSPACE);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={styles['create-workspace-icon']}
                >
                  <path
                    d="M8 2.667v10.666M2.667 8h10.666"
                    stroke="#8F97A5"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span className={styles['create-workspace-text']}>
                  Create workspace
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

WorkspaceSwitcher.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
};

export default WorkspaceSwitcher;
